import { Button, DialogBody, DialogFooter, SkeletonSpinnerCentered } from '@civilgrid/ui/components';
import { useParams } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { get811Data as apiGet811Data } from '~/api/misc2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';

interface Props {
  closeDialog: () => void;
}

export default function UtilityContactsModal({ closeDialog }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [contacts, setContacts] = useState<any[]>([]);
  const { projectId } = useParams({ strict: false }) as { projectId: string };

  const containerDiv = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    setError(false);
    setErrorMessage('No error');
    if (!projectId) {
      setError(true);
      setLoading(false);
      return;
    }

    try {
      const res = await apiGet811Data(projectId);

      if (!res.coverage) {
        setError(true);
        setErrorMessage('Utility contacts not available in this area.');
        setLoading(false);
        return;
      }

      setContacts([...res.results]);
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching utility contacts: ', err);
      setError(true);
      setErrorMessage('There was an error fetching utility contacts, please contact support.');
      setLoading(false);
      return;
    }
  };

  const downloadCSV = () => {
    let csvData: string = 'Company Name, Group, Name, Email, Phone, Comments,\n';

    contacts.forEach((c) => {
      const name = c.name?.endsWith('\r') ? c.name.split('\r')[0] : c.name ? c.name : '';
      const s = `${name || ''}, ${c.contact_type || c.name || ''}, ${
        c.contact || ''
      }, ${c.email || ''}, ${c.phone || c.cell || ''}, ${c.contacts || ''},\n`;

      csvData += s;
    });

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = 'utility_contacts.csv';

    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    link.click();

    capturePosthogEvent(POSTHOG_EVENTS.utilitiesContactsDownloaded, {
      project_id: projectId,
    });

    closeDialog();
  };

  if (loading) {
    return (
      <DialogBody>
        <SkeletonSpinnerCentered />
      </DialogBody>
    );
  }

  if (error) {
    return (
      <DialogBody>
        <div>{errorMessage}</div>
      </DialogBody>
    );
  }

  const sortContacts = (columnName: 'name' | 'contact_type' | 'contact' | 'email' | 'phone' | 'contacts') => {
    const prev = [...contacts];

    // check to see if list is already sorted alphabetically.
    const isSortedAlphabetically = prev.every((item, i, array) => {
      if (i === 0) {
        return true;
      }

      if (typeof item[columnName] === 'string') {
        return item[columnName].localeCompare(array[i - 1][columnName]) >= 0;
      } else {
        return true;
      }
    });

    const newOrder = [...contacts];

    // if the list has already been sorted alphabetically by the specified column name,
    // then we want to sort by reverse-alphabetical order on the second click on the column.
    if (isSortedAlphabetically) {
      newOrder.sort((a, b) => {
        if (!a[columnName]) return 1;
        if (!b[columnName]) return -1;

        if (typeof a[columnName] === 'string') {
          return b[columnName].localeCompare(a[columnName]);
        } else if (typeof a[columnName] === 'number') {
          return b[columnName] - a[columnName];
        }

        return 0;
      });
    } else {
      newOrder.sort((a, b) => {
        if (typeof a[columnName] === 'string') {
          return a[columnName].localeCompare(b[columnName]);
        } else if (typeof a[columnName] === 'number') {
          return a[columnName] - b[columnName];
        }
        return 1; // should never really occur
      });
    }

    setContacts([...newOrder]);
  };

  const theadClasses = 'cursor-pointer hover:bg-grey-20 mx-4 rounded';

  return (
    <>
      <DialogBody className="text-sm">
        <div ref={containerDiv} className="max-h-[75vh] w-[66vw] max-w-7xl overflow-auto">
          <table className="relative w-full table-auto">
            <thead className="w-full font-bold">
              <tr className="justify-between border-b border-b-grey-20">
                <td className={theadClasses} onClick={() => sortContacts('name')}>
                  Company Name
                </td>
                <td className={theadClasses} onClick={() => sortContacts('contact_type')}>
                  Group
                </td>
                <td className={theadClasses} onClick={() => sortContacts('contact')}>
                  Name
                </td>
                <td className={theadClasses} onClick={() => sortContacts('email')}>
                  Email
                </td>
                <td className={theadClasses} onClick={() => sortContacts('phone')}>
                  Phone
                </td>
                <td className={theadClasses} onClick={() => sortContacts('contacts')}>
                  Comments
                </td>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact.OBJECTID} className="border-b border-b-grey-20">
                  <td>{contact.name || ''}</td>
                  <td>{contact.contact_type || ''}</td>
                  <td>{contact.contact || ''}</td>
                  <td>{contact.email || ''}</td>
                  <td>{contact.phone || contact.cell || ''}</td>
                  <td>{contact.contacts || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button onClick={downloadCSV} data-testid="utility-contacts-download-csv-button">
          Download Full CSV
        </Button>
      </DialogFooter>
    </>
  );
}
